// Renders a certificate's HTML into a hidden iframe, rasterizes it with
// html2canvas and saves it as an A4 PDF via jspdf. Both libraries are
// dynamically imported so they stay out of the main bundle until a user
// actually downloads a certificate.

// A4 at 96dpi. The iframe is laid out at this width so the capture matches
// what the print window produces on paper.
const A4_WIDTH_PX = 794;
const A4_HEIGHT_PX = 1123;

const IMAGE_LOAD_TIMEOUT_MS = 10_000;

const waitForImages = async (doc: Document) => {
  const pending = Array.from(doc.images)
    .filter((img) => !img.complete)
    .map(
      (img) =>
        new Promise<void>((resolve) => {
          img.addEventListener('load', () => resolve(), { once: true });
          img.addEventListener('error', () => resolve(), { once: true });
        })
    );
  if (pending.length === 0) return;

  await Promise.race([
    Promise.all(pending),
    new Promise<void>((resolve) => setTimeout(resolve, IMAGE_LOAD_TIMEOUT_MS))
  ]);
};

export const normalizeInlineBlockTextForRaster = (doc: Document) => {
  const win = doc.defaultView;
  const body = doc.body;
  if (!win || !body) return;
  for (const element of Array.from(body.querySelectorAll<HTMLElement>('*'))) {
    const display = win.getComputedStyle(element).display;
    if (display !== 'inline-block' && display !== 'inline-table') continue;
    for (const child of Array.from(element.children) as HTMLElement[]) {
      if (!child.style || win.getComputedStyle(child).display !== 'block') continue;
      child.style.display = 'inline-block';
      if (!child.style.width) child.style.width = '100%';
    }
  }
};

const MIN_SINGLE_PAGE_SCALE = 0.6;

type PagePlan =
  | { mode: 'single'; widthMm: number; heightMm: number; offsetXMm: number }
  | { mode: 'multi'; pageHeightPx: number };

export const planCertificatePages = ({
  canvasWidth,
  canvasHeight,
  pageWidthMm,
  pageHeightMm
}: {
  canvasWidth: number;
  canvasHeight: number;
  pageWidthMm: number;
  pageHeightMm: number;
}): PagePlan => {
  const pageHeightPx = Math.floor((canvasWidth * pageHeightMm) / pageWidthMm);
  if (canvasHeight <= pageHeightPx) {
    return {
      mode: 'single',
      widthMm: pageWidthMm,
      heightMm: (canvasHeight * pageWidthMm) / canvasWidth,
      offsetXMm: 0
    };
  }
  const fitScale = pageHeightPx / canvasHeight;
  if (fitScale >= MIN_SINGLE_PAGE_SCALE) {
    const widthMm = (canvasWidth / canvasHeight) * pageHeightMm;
    return { mode: 'single', widthMm, heightMm: pageHeightMm, offsetXMm: (pageWidthMm - widthMm) / 2 };
  }
  return { mode: 'multi', pageHeightPx };
};

const BLANK_CHANNEL_MIN = 250;

export const findCleanBreakRow = (
  pixels: { data: Uint8ClampedArray | number[]; width: number; height: number },
  targetY: number,
  minY: number
): number => {
  const { data, width } = pixels;
  const isBlankRow = (y: number) => {
    for (let x = 0; x < width; x++) {
      const i = (y * width + x) * 4;
      if (
        data[i] < BLANK_CHANNEL_MIN ||
        data[i + 1] < BLANK_CHANNEL_MIN ||
        data[i + 2] < BLANK_CHANNEL_MIN
      ) {
        return false;
      }
    }
    return true;
  };
  for (let y = targetY; y > minY; y--) {
    if (isBlankRow(y)) return y;
  }
  return targetY;
};

export const downloadCertificatePdf = async (
  html: string,
  suggestedFileName?: string
): Promise<boolean> => {
  if (!html) return false;

  const iframe = document.createElement('iframe');
  iframe.setAttribute('aria-hidden', 'true');
  iframe.style.position = 'fixed';
  iframe.style.left = '-10000px';
  iframe.style.top = '0';
  iframe.style.width = `${A4_WIDTH_PX}px`;
  iframe.style.height = `${A4_HEIGHT_PX}px`;
  iframe.style.border = '0';
  document.body.appendChild(iframe);

  try {
    const doc = iframe.contentDocument;
    if (!doc) return false;

    doc.open();
    doc.write(html);
    doc.close();
    await waitForImages(doc);
    normalizeInlineBlockTextForRaster(doc);

    const [{ default: html2canvas }, { jsPDF }] = await Promise.all([
      import('html2canvas'),
      import('jspdf')
    ]);

    // useCORS lets html2canvas include the logo/signature/QR images, which are
    // served from Supabase storage and api.qrserver.com with permissive CORS.
    const canvas = await html2canvas(doc.body, {
      scale: 2,
      useCORS: true,
      backgroundColor: '#ffffff',
      windowWidth: A4_WIDTH_PX
    });
    if (!canvas.width || !canvas.height) return false;

    const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
    const pageWidthMm = pdf.internal.pageSize.getWidth();
    const pageHeightMm = pdf.internal.pageSize.getHeight();
    const plan = planCertificatePages({
      canvasWidth: canvas.width,
      canvasHeight: canvas.height,
      pageWidthMm,
      pageHeightMm
    });

    if (plan.mode === 'single') {
      pdf.addImage(
        canvas.toDataURL('image/jpeg', 0.95),
        'JPEG',
        plan.offsetXMm,
        0,
        plan.widthMm,
        plan.heightMm
      );
    } else {
      const sourceCtx = canvas.getContext('2d');
      if (!sourceCtx) return false;
      const pixels = sourceCtx.getImageData(0, 0, canvas.width, canvas.height);
      const { pageHeightPx } = plan;

      let renderedHeight = 0;
      let pageIndex = 0;
      while (renderedHeight < canvas.height) {
        let sliceEnd = Math.min(renderedHeight + pageHeightPx, canvas.height);
        if (sliceEnd < canvas.height) {
          const minY = renderedHeight + Math.floor(pageHeightPx / 2);
          sliceEnd = findCleanBreakRow(pixels, sliceEnd, minY);
        }
        const sliceHeight = sliceEnd - renderedHeight;
        const slice = document.createElement('canvas');
        slice.width = canvas.width;
        slice.height = sliceHeight;
        const ctx = slice.getContext('2d');
        if (!ctx) return false;
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, slice.width, slice.height);
        ctx.drawImage(
          canvas,
          0,
          renderedHeight,
          canvas.width,
          sliceHeight,
          0,
          0,
          canvas.width,
          sliceHeight
        );

        if (pageIndex > 0) pdf.addPage();
        pdf.addImage(
          slice.toDataURL('image/jpeg', 0.95),
          'JPEG',
          0,
          0,
          pageWidthMm,
          (sliceHeight * pageWidthMm) / canvas.width
        );

        renderedHeight = sliceEnd;
        pageIndex += 1;
      }
    }

    const fileName = String(suggestedFileName || 'certificate').trim() || 'certificate';
    pdf.save(`${fileName}.pdf`);
    return true;
  } catch {
    return false;
  } finally {
    iframe.remove();
  }
};
